'use client';

import { css, Z_INDEX_LIBRARY } from '@bts-web/utils-style-engine';
import { type Language } from '@bts-web/utils-lokalise';
import { useChangeLanguage } from '../../../language';
import { LanguageChangeMenu } from '../../../language/LanguageChangeMenu/LanguageChangeMenu.use-client';
import { getAppConfig } from '@bts-web/core-app-config';
import { useDrawerContext } from '../../components/ModalDrawerContext/ModalDrawerContext';
import { Drawer, DrawerTriggerButton } from '../../../common/components/Drawer';
import { FilledSpinnerIos, RegularGlobe } from '../../svgIcons';
import { useClientUserInfo } from '../../userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';

export type MobileLanguageDrawerTranslations = {
  errorTitle: string;
  errorSubtitle: string;
  germanSubtitle: string;
  englishSubtitle: string;
  arabicSubtitle: string;
  frenchSubtitle: string;
  title: string;
};

interface MobileLanguageDrawerProps {
  translations: MobileLanguageDrawerTranslations;
  contrastVariant?: boolean;
}

const languageDrawerId = 'mobile-language-drawer';

const MobileLanguageDrawer = ({
  translations,
  contrastVariant,
}: MobileLanguageDrawerProps) => {
  const { closeDrawer } = useDrawerContext();

  const { landingPageLangIconColorScheme } = getAppConfig();

  const { onChangeLanguage, isUserUpdateLoading } = useChangeLanguage({
    translations: {
      errorSubtitle: translations.errorSubtitle,
      errorTitle: translations.errorTitle,
    },
  });

  const { language } = useClientUserInfo();

  const onLanguageValueChange = (languageValue: string) => {
    onChangeLanguage(languageValue as Language);

    closeDrawer();
  };

  const LanguageDrawer = () => (
    <Drawer data-testid={languageDrawerId}>
      <div className={css({ minHeight: 300 })}>
        <LanguageChangeMenu
          translations={translations}
          onLanguageValueChange={onLanguageValueChange}
          currentValue={language}
        />
      </div>
    </Drawer>
  );

  return (
    <DrawerTriggerButton
      DrawerComponent={LanguageDrawer}
      drawerProps={{
        position: 'bottom',
        title: translations.title,
        style: {
          zIndex: Z_INDEX_LIBRARY.LANGUAGE_DRAWER,
        },
      }}
    >
      <button
        type="button"
        data-testid="mobile-language-button"
        className={css({
          cursor: 'pointer',
          color: contrastVariant ? 'brand.on_fill_primary' : 'inherit',
        })}
        disabled={isUserUpdateLoading}
      >
        {isUserUpdateLoading ? (
          <FilledSpinnerIos
            aria-label="language change loading icon"
            data-element="loading-icon"
            className={
              landingPageLangIconColorScheme === 'dark'
                ? css({ color: 'neutrals.text_primary' })
                : css({ color: 'neutrals.text_secondary' })
            }
          />
        ) : (
          <RegularGlobe
            className={
              landingPageLangIconColorScheme === 'dark'
                ? css({ color: 'neutrals.text_primary' })
                : css({ color: 'neutrals.text_secondary' })
            }
          />
        )}
      </button>
    </DrawerTriggerButton>
  );
};

export { MobileLanguageDrawer };
